import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { isIP } from 'is-ip';

// Shared page styles
import {
  RightNavContainer,
  RightNavContainerFoot,
  RightNavContainerX,
  RightNavContainerXPar,
  RightNavSecDiv,
  RNCFBtn,
  RNCFBtn2,
  SBMSDiv3,
  SBMSInput2,
  SDMHeading,
  SDMHeadingP,
  SideBarMainDiv,
  TeamHeadBtnImgP,
  TeamHeadBtnImgPa,
} from '../../styles';

// Component-local styles
import {
  SectionCard,
  SectionHeading,
  FieldGroup,
  ErrorText,
  StatusText,
  SuccessText,
  RobotCard,
  RobotCardHeader,
  RobotTitle,
  RemoveButton,
  Chevron,
  AddRobotRow,
  CheckboxRow,
  FormatRow,
} from './styles';

// Config
import {
  Int_Rec_Format_Options2,
  Rec_Format_Options1,
  Rec_Format_Options2,
  ext_Rec_Format_Options3,
  int_Rec_Format_Options3,
} from 'Config/permissionConstant';

// PNG
import cross from 'Assets/images/cross.png';

// Redux actions
import {
  apiBulkOnboardOrgRobotRequest,
  apiValidateRobotAirIdRequest,
  apiClearRobotAirIdValidation,
  apiClearBulkOnboardResult,
  apiGetAllOrgRobotsRequest,
} from 'Redux/actions/gear';

const AIR_ID_LEN = 14;

const formatAirId = inputString => {
  const digits = inputString.replace(/-/g, '');
  const chunks = Math.ceil(digits.length / 4);
  let result = '';
  for (let i = 0; i < chunks; i++) {
    result += i !== chunks - 1 ? digits.substring(i * 4, i * 4 + 4) + '-' : digits.substring(i * 4);
  }
  return result;
};

const OnboardRobot = ({ onChangeRightNav, organisationId, organisationName }) => {
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const gears = useSelector(state => state.gear);
  const validation = gears?.robotAirIdValidation || {};

  // Per-instance key counter; resets on mount, never mutates during render.
  const keySeq = useRef(0);
  const newRobotRow = () => ({ key: `r${keySeq.current++}`, air_id: '', expanded: true });

  // Group 1-2: customer (editable, prefilled from the logged-in user + org)
  const [customer, setCustomer] = useState({
    contact_name: `${auth?.user_information?.first_name || ''} ${auth?.user_information?.last_name || ''}`.trim(),
    contact_email: auth?.user_information?.email || auth?.user?.email || '',
    contact_phone: '',
    company_name: organisationName || '',
  });
  // Group 2b: mailing address
  const [address, setAddress] = useState({
    address_line_1: '',
    address_line_2: '',
    city: '',
    state_province_region: '',
    zip_postal_code: '',
    country: '',
  });
  // Group 3: purchase
  const [purchase, setPurchase] = useState({ purchase_date: '', reseller_name: '' });
  // Group 4: expandable list of robots
  const [robots, setRobots] = useState(() => [newRobotRow()]);
  // Group 5: shared config
  const [sharedConfig, setSharedConfig] = useState({
    default_auto_update: true,
    default_lan_ip: '',
    default_public_ip: '',
    default_auto_update_stream_ip: true,
    default_stream_destination_ip: '',
  });
  const [internal_record, set_internal_record] = useState({ part1: '1080', part2: '23.98', part3: 'h.264' });
  const [external_stream, set_external_stream] = useState({ part1: '1080', part2: 'p24', part3: 'h.264' });

  const [error, setError] = useState({});

  // Set once any robot is onboarded, so closing the sidebar refetches the list
  // even on a partially-successful batch closed manually.
  const didOnboardRef = useRef(false);

  const closeSideBar = () => {
    onChangeRightNav('');
    dispatch(apiClearRobotAirIdValidation());
    // Discard any batch result so a reopened sidebar starts clean.
    dispatch(apiClearBulkOnboardResult());
    // Only refetch if something actually changed; a plain Cancel is a no-op.
    if (didOnboardRef.current) {
      dispatch(apiGetAllOrgRobotsRequest(auth.token, organisationId));
      didOnboardRef.current = false;
    }
  };

  // ---- Robot row helpers -------------------------------------------------
  const updateRobot = (key, patch) =>
    // Editing a row clears any stale server-side error from the last submit.
    setRobots(prev => prev.map(r => (r.key === key ? { ...r, ...patch, serverError: undefined } : r)));

  const onAirIdChange = (key, value) => {
    const airId = formatAirId(value);
    updateRobot(key, { air_id: airId });
    // Editing invalidates the last submit's error for this row.
    setError(prev => {
      if (!prev?.robots?.[key]) return prev;
      const robots = { ...prev.robots };
      delete robots[key];
      return { ...prev, robots };
    });
    // Same check as the single-onboard flow: validate against inventory once complete.
    if (airId.length === AIR_ID_LEN) {
      dispatch(apiValidateRobotAirIdRequest(auth.token, airId));
    }
  };

  const addRobot = () => setRobots(prev => [...prev, newRobotRow()]);
  const removeRobot = key => setRobots(prev => (prev.length > 1 ? prev.filter(r => r.key !== key) : prev));
  const toggleRobot = key => setRobots(prev => prev.map(r => (r.key === key ? { ...r, expanded: !r.expanded } : r)));

  // ---- Shared record-format strings -------------------------------------
  useEffect(() => {
    const { part1, part2, part3 } = internal_record;
    setSharedConfig(prev => ({
      ...prev,
      default_internal_record_format: part1 && part2 && part3 ? `${part1}/${part2}/${part3}` : '',
    }));
  }, [internal_record]);

  useEffect(() => {
    const { part1, part2, part3 } = external_stream;
    setSharedConfig(prev => ({
      ...prev,
      default_external_stream_format: part1 && part2 && part3 ? `${part1}/${part2}/${part3}` : '',
    }));
  }, [external_stream]);

  // ---- Reconcile the form with the batch result (only for our own submit) ----
  useEffect(() => {
    if (gears?.bulkOnboardResult) {
      const { results = [], failed, created = 0 } = gears.bulkOnboardResult;

      // Remember a real onboard so closeSideBar refetches later.
      if (created > 0) didOnboardRef.current = true;

      // Nothing failed -> every row was onboarded or already onboarded; close.
      if (!failed) {
        closeSideBar();
        return;
      }

      // Keep only failed rows, attaching each one's error; drop the succeeded ones.
      const errorByAirId = {};
      results.forEach(r => {
        if (r.status === 'error') errorByAirId[r.air_id] = r.error;
      });
      setRobots(prev =>
        prev
          .filter(row => errorByAirId[row.air_id] !== undefined)
          .map(row => ({ ...row, expanded: true, serverError: errorByAirId[row.air_id] })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gears?.bulkOnboardResult]);

  // ---- Validation --------------------------------------------------------
  const validateAll = () => {
    const newErrors = { robots: {} };

    // First row keeps a duplicated AIR ID; every later repeat is flagged.
    const seenAirIds = new Set();
    robots.forEach(r => {
      const rowErr = {};
      if (!r.air_id) {
        rowErr.air_id = 'This field is required';
      } else if (r.air_id.length !== AIR_ID_LEN) {
        rowErr.air_id = 'Please provide a valid Air Id';
      } else if (seenAirIds.has(r.air_id)) {
        rowErr.air_id = 'Duplicate AIR ID — already entered in another row';
      } else if (!validation[r.air_id]?.valid) {
        rowErr.air_id = 'Invalid AIR ID. Contact A.I.R. to confirm your AIR ID';
      }
      seenAirIds.add(r.air_id);
      // No nickname check: it is inventory-supplied, and a valid AIR ID always carries one.
      if (Object.keys(rowErr).length) newErrors.robots[r.key] = rowErr;
    });

    if (sharedConfig.default_lan_ip && !isIP(sharedConfig.default_lan_ip)) {
      newErrors.default_lan_ip = 'Invalid LAN IP address';
    }
    if (sharedConfig.default_public_ip && !isIP(sharedConfig.default_public_ip)) {
      newErrors.default_public_ip = 'Invalid WAN IP address';
    }
    if (sharedConfig.default_stream_destination_ip && !isIP(sharedConfig.default_stream_destination_ip)) {
      newErrors.default_stream_destination_ip = 'Invalid Stream Destination IP address';
    }
    if (!sharedConfig.default_internal_record_format) {
      newErrors.internal_record = 'Internal record format must be set';
    }
    if (!sharedConfig.default_external_stream_format) {
      newErrors.external_stream = 'External Stream format must be set';
    }

    setError(newErrors);
    return Object.keys(newErrors.robots).length === 0 &&
      Object.keys(newErrors).filter(k => k !== 'robots').length === 0;
  };

  const onConfirm = () => {
    if (!validateAll()) {
      toast.error('Please fix the highlighted fields before onboarding.');
      return;
    }
    const payload = {
      organisation_id: organisationId,
      contact_name: customer.contact_name,
      contact_email: customer.contact_email,
      phone_number: customer.contact_phone,
      company_name: customer.company_name,
      address_line_1: address.address_line_1,
      address_line_2: address.address_line_2,
      city: address.city,
      state_province_region: address.state_province_region,
      zip_postal_code: address.zip_postal_code,
      country: address.country,
      purchase_date: purchase.purchase_date || null,
      reseller_name: purchase.reseller_name,
      shared_config: {
        default_auto_update: sharedConfig.default_auto_update,
        default_lan_ip: sharedConfig.default_lan_ip,
        default_public_ip: sharedConfig.default_public_ip,
        default_auto_update_stream_ip: sharedConfig.default_auto_update_stream_ip,
        default_stream_destination_ip: sharedConfig.default_stream_destination_ip,
        default_internal_record_format: sharedConfig.default_internal_record_format,
        default_external_stream_format: sharedConfig.default_external_stream_format,
      },
      robots: robots.map(r => ({ air_id: r.air_id })),
    };
    dispatch(apiBulkOnboardOrgRobotRequest(auth.token, payload));
  };

  const errMsg = msg => msg && <ErrorText>{msg}</ErrorText>;

  return (
    <RightNavContainer>
      <RightNavContainerX onClick={closeSideBar}>
        <RightNavContainerXPar src={cross} />
      </RightNavContainerX>

      <SideBarMainDiv>
        <SDMHeading>Register & Onboard Robots</SDMHeading>
        <RightNavSecDiv style={{ padding: '5px', marginBottom: '70px', paddingBottom: '50px' }}>

          <SectionCard>
            <SectionHeading>Contact</SectionHeading>
            <FieldGroup>
              <SDMHeadingP>Contact Name</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="text"
                  placeholder="Contact Name"
                  value={customer.contact_name}
                  onChange={e => setCustomer(prev => ({ ...prev, contact_name: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>Contact Email</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="email"
                  placeholder="Contact Email"
                  value={customer.contact_email}
                  onChange={e => setCustomer(prev => ({ ...prev, contact_email: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>Contact Phone</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="tel"
                  placeholder="Contact Phone"
                  maxLength={30}
                  value={customer.contact_phone}
                  onChange={e => setCustomer(prev => ({ ...prev, contact_phone: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>Company Name</SDMHeadingP>
              {/* Read-only: derived from the organisation, not editable here. */}
              <SBMSDiv3 style={{ cursor: 'not-allowed' }}>
                <SBMSInput2
                  type="text"
                  placeholder="Company Name"
                  value={customer.company_name}
                  readOnly
                  style={{ cursor: 'not-allowed' }}
                />
              </SBMSDiv3>
            </FieldGroup>
          </SectionCard>

          <SectionCard>
            <SectionHeading>Mailing Address</SectionHeading>
            <FieldGroup>
              <SDMHeadingP>Address Line 1</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="text"
                  placeholder="Address Line 1"
                  maxLength={255}
                  value={address.address_line_1}
                  onChange={e => setAddress(prev => ({ ...prev, address_line_1: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>Address Line 2</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="text"
                  placeholder="Address Line 2"
                  maxLength={255}
                  value={address.address_line_2}
                  onChange={e => setAddress(prev => ({ ...prev, address_line_2: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>City</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="text"
                  placeholder="City"
                  maxLength={100}
                  value={address.city}
                  onChange={e => setAddress(prev => ({ ...prev, city: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>State / Province / Region</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="text"
                  placeholder="State / Province / Region"
                  maxLength={100}
                  value={address.state_province_region}
                  onChange={e => setAddress(prev => ({ ...prev, state_province_region: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>ZIP / Postal Code</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="text"
                  placeholder="ZIP / Postal Code"
                  maxLength={20}
                  value={address.zip_postal_code}
                  onChange={e => setAddress(prev => ({ ...prev, zip_postal_code: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>Country</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="text"
                  placeholder="Country"
                  maxLength={100}
                  value={address.country}
                  onChange={e => setAddress(prev => ({ ...prev, country: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
          </SectionCard>

          <SectionCard>
            <SectionHeading>Purchase Details</SectionHeading>
            <FieldGroup>
              <SDMHeadingP>Purchase Date</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="date"
                  value={purchase.purchase_date}
                  onChange={e => setPurchase(prev => ({ ...prev, purchase_date: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
            <FieldGroup>
              <SDMHeadingP>Reseller Name</SDMHeadingP>
              <SBMSDiv3>
                <SBMSInput2
                  type="text"
                  placeholder="Enter reseller name"
                  maxLength={255}
                  value={purchase.reseller_name}
                  onChange={e => setPurchase(prev => ({ ...prev, reseller_name: e.target.value }))}
                />
              </SBMSDiv3>
            </FieldGroup>
          </SectionCard>

          <SectionCard>
            <SectionHeading>Robots</SectionHeading>
            {robots.map((r, idx) => {
              const v = validation[r.air_id];
              const rowErr = error?.robots?.[r.key] || {};
              return (
                <RobotCard key={r.key}>
                  <RobotCardHeader onClick={() => toggleRobot(r.key)}>
                    <RobotTitle>{`Robot ${idx + 1}${r.air_id ? ` — ${r.air_id}` : ''}`}</RobotTitle>
                    <div>
                      {robots.length > 1 && (
                        <RemoveButton onClick={e => { e.stopPropagation(); removeRobot(r.key); }}>
                          Remove
                        </RemoveButton>
                      )}
                      <Chevron>{r.expanded ? '▲' : '▼'}</Chevron>
                    </div>
                  </RobotCardHeader>

                  {r.expanded && (
                    <FieldGroup>
                      <SDMHeadingP>AIR ID</SDMHeadingP>
                      <SBMSDiv3>
                        <SBMSInput2
                          type="text"
                          placeholder="0000-0000-0000"
                          maxLength={AIR_ID_LEN}
                          value={r.air_id}
                          onChange={e => onAirIdChange(r.key, e.target.value)}
                        />
                      </SBMSDiv3>
                      {v?.loading && <StatusText>Checking AIR ID…</StatusText>}
                      {v && !v.loading && v.valid && <SuccessText>Valid AIR ID</SuccessText>}
                      {/* Confirm-time error takes over; never show both (same message). */}
                      {v && !v.loading && !v.valid && !rowErr.air_id &&
                        errMsg('Invalid AIR ID. Contact A.I.R. to confirm your AIR ID')}
                      {errMsg(rowErr.air_id)}
                      {r.serverError && <ErrorText>{r.serverError}</ErrorText>}

                      {/* Inventory-supplied, not editable here — renameable later from the
                          robot's settings once onboarded. */}
                      <FieldGroup>
                        <SDMHeadingP>Robot Name</SDMHeadingP>
                        <SBMSDiv3>
                          <SBMSInput2
                            type="text"
                            placeholder="Set once the AIR ID is verified"
                            value={v?.valid ? v.nickName : ''}
                            readOnly
                            disabled
                          />
                        </SBMSDiv3>
                      </FieldGroup>
                    </FieldGroup>
                  )}
                </RobotCard>
              );
            })}
            <AddRobotRow>
              <TeamHeadBtnImgP onClick={addRobot}>+ Add another robot</TeamHeadBtnImgP>
            </AddRobotRow>
          </SectionCard>

          <SectionCard>
            <SectionHeading>Configuration (applies to all robots)</SectionHeading>

            <CheckboxRow>
              <input
                type="checkbox"
                className="form-check-input"
                id="default_auto_update"
                checked={sharedConfig.default_auto_update}
                onChange={e => setSharedConfig(prev => ({ ...prev, default_auto_update: e.target.checked }))}
              />
              <label className="form-label form-check-label" htmlFor="default_auto_update">
                Auto Update Lan IP / WAN IP
              </label>
            </CheckboxRow>

            <FieldGroup>
              <SDMHeadingP>Default Lan IP</SDMHeadingP>
              <input
                type="text"
                className="form-control"
                placeholder="Example: 123.123.123.123"
                maxLength={30}
                value={sharedConfig.default_lan_ip}
                onChange={e => setSharedConfig(prev => ({ ...prev, default_lan_ip: e.target.value }))}
              />
              {errMsg(error.default_lan_ip)}
            </FieldGroup>

            <FieldGroup>
              <SDMHeadingP>Default WAN IP</SDMHeadingP>
              <input
                type="text"
                className="form-control"
                placeholder="Example: 123.123.123.123"
                maxLength={30}
                value={sharedConfig.default_public_ip}
                onChange={e => setSharedConfig(prev => ({ ...prev, default_public_ip: e.target.value }))}
              />
              {errMsg(error.default_public_ip)}
            </FieldGroup>

            <CheckboxRow>
              <input
                type="checkbox"
                className="form-check-input"
                id="default_auto_update_stream_ip"
                checked={sharedConfig.default_auto_update_stream_ip}
                onChange={e => setSharedConfig(prev => ({ ...prev, default_auto_update_stream_ip: e.target.checked }))}
              />
              <label className="form-label form-check-label" htmlFor="default_auto_update_stream_ip">
                Auto Update Stream Destination IP
              </label>
            </CheckboxRow>

            <FieldGroup>
              <SDMHeadingP>Default Stream Destination IP</SDMHeadingP>
              <input
                type="text"
                className="form-control"
                placeholder="Example: 123.123.123.123"
                maxLength={30}
                value={sharedConfig.default_stream_destination_ip}
                onChange={e => setSharedConfig(prev => ({ ...prev, default_stream_destination_ip: e.target.value }))}
              />
              {errMsg(error.default_stream_destination_ip)}
            </FieldGroup>

            <FieldGroup>
              <SDMHeadingP>Internal Record Format</SDMHeadingP>
              <FormatRow>
                <select className="form-select" value={internal_record.part1}
                  onChange={e => set_internal_record(prev => ({ ...prev, part1: e.target.value }))}>
                  {Rec_Format_Options1.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
                </select>
                <select className="form-select" value={internal_record.part2}
                  onChange={e => set_internal_record(prev => ({ ...prev, part2: e.target.value }))}>
                  {Int_Rec_Format_Options2.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
                </select>
                <select className="form-select" value={internal_record.part3}
                  onChange={e => set_internal_record(prev => ({ ...prev, part3: e.target.value }))}>
                  {int_Rec_Format_Options3.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
                </select>
              </FormatRow>
              {errMsg(error.internal_record)}
            </FieldGroup>

            <FieldGroup>
              <SDMHeadingP>External Stream Format</SDMHeadingP>
              <FormatRow>
                <select className="form-select" value={external_stream.part1}
                  onChange={e => set_external_stream(prev => ({ ...prev, part1: e.target.value }))}>
                  {Rec_Format_Options1.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
                </select>
                <select className="form-select" value={external_stream.part2}
                  onChange={e => set_external_stream(prev => ({ ...prev, part2: e.target.value }))}>
                  {Rec_Format_Options2.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
                </select>
                <select className="form-select" value={external_stream.part3}
                  onChange={e => set_external_stream(prev => ({ ...prev, part3: e.target.value }))}>
                  {ext_Rec_Format_Options3.map(item => <option value={item.value} key={item.value}>{item.label}</option>)}
                </select>
              </FormatRow>
              {errMsg(error.external_stream)}
            </FieldGroup>
          </SectionCard>
        </RightNavSecDiv>
      </SideBarMainDiv>

      <RightNavContainerFoot>
        <RNCFBtn>
          <TeamHeadBtnImgP onClick={closeSideBar}>Cancel</TeamHeadBtnImgP>
        </RNCFBtn>
        <RNCFBtn2>
          <TeamHeadBtnImgPa onClick={onConfirm}>Confirm</TeamHeadBtnImgPa>
        </RNCFBtn2>
      </RightNavContainerFoot>
    </RightNavContainer>
  );
};

export default OnboardRobot;
